import { Line } from '@react-three/drei'
import { useEffect, useMemo } from 'react'
import { Vetor3D } from '../componentes/Vetor3D'
import { multiplicar, paraArray, somar, vetor, type Vetor3 } from '../../dominio/geometria/vetor3'
import { ancoraPorId } from '../../dominio/didatico/perfilVeiculo'
import { perfilVitimaPorId } from '../../dominio/didatico/perfisVitima'
import { corpoSoltoPorId, type CorpoSolto } from '../../dominio/didatico/cargaSolta'
import { itensDeCarga, origensDaCarga } from '../../dominio/didatico/cargaSoltaColocacao'
import { perfilVitimaPorId as proporcoesPorId } from '../../dominio/didatico/perfisVitima'
import { useFisicaCarga } from '../../fisica/useFisicaCarga'
import type { AprisionamentoMembro, EntradaSolucionador } from '../../fisica/solucionador'
import {
  amostrarTrajetoria,
  idSegmentoRagdoll,
  trajetoriaPorId,
  type QuadroCorpo,
  type TrajetoriaCorpo,
} from '../../fisica/trajetoria'
import type { MapaEstados } from '../../dominio/didatico/estadoPecas'
import type { Ocupante } from '../../dominio/didatico/ocupantes'
import { POSE_SENTADA, posicaoCabeca } from '../../dominio/didatico/rigVitima'
import {
  origemDeContatoPadrao,
  superficiesDoHabitaculo,
  type SuperficieInterna,
} from '../../dominio/didatico/superficiesInternas'
import { reacaoDoOcupante, type ReacaoOcupante } from '../../dominio/didatico/retencaoOcupante'
import {
  amostrarCinematica,
  colisao,
  instantesDeContato,
  paraMundo,
  posicaoDoVeiculo,
  type TipoColisao,
} from '../../dominio/didatico/timelineCinematica'
import { haContatoFrontal } from '../../dominio/didatico/tiposDeImpacto'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { useMaquete } from '../componentes/contextoMaquete'
import { RodaRealista } from '../veiculo/RodaRealista'
import { SuspensaoVeicular } from '../veiculo/SuspensaoVeicular'
import { ProvedorDeformacao } from '../veiculo/contextoDeformacao'
import { VeiculoDidatico } from '../veiculo/VeiculoDidatico'
import { OcupanteSelecionavel } from '../componentes/OcupanteSelecionavel'
import { VitimaProcedural } from '../vitima/VitimaProcedural'
import { CorpoSoltoProcedural } from '../vitima/CorpoSoltoProcedural'
import { VitimaRagdoll } from '../vitima/VitimaRagdoll'
import { AlvoImpacto3D } from './AlvoImpacto3D'
import type { TipoAlvoImpacto } from '../../dominio/didatico/alvosImpacto'
import { deformacaoDoEvento } from '../../dominio/didatico/deformacaoDoEvento'
import { estadoDeformacaoNoInstante } from '../../dominio/didatico/campoDeformacao'
import {
  CLASSES_VEICULO,
  ID_VEICULO_PRINCIPAL,
  ajustePosImpactoA,
  type ConfiguracaoOutroVeiculo,
} from '../../dominio/didatico/colisaoEntreVeiculos'
import { DestaqueImpacto3D } from './DestaqueImpacto3D'
import { avaliarAtencaoCorporal, type AtencaoCorporal } from '../../dominio/didatico/atencaoCorporal'
import { DeformacaoImpacto3D } from './DeformacaoImpacto3D'
import type { MembroAprisionavel } from '../../dominio/didatico/aprisionamentoMembro'
import type { LeituraBiomecanicaInstante, TonusOcupante } from '../../dominio/didatico/tonusOcupante'
import { calcularDinamicaSuspensao } from '../../dominio/didatico/suspensao'
import { PistasCena3D } from './PistasCena3D'
import type { PistaCena } from '../../dominio/didatico/pistasCena'
import {
  obterAjusteCinematicaVariacao,
  type LadoImpactoLateral,
  type TrajetoriaFrontal,
} from '../../dominio/didatico/variacoesCinematica'

const SEGMENTOS_MEMBROS: readonly MembroAprisionavel[] = [
  'bracoEsquerdo',
  'bracoDireito',
  'antebracoEsquerdo',
  'antebracoDireito',
  'coxaEsquerda',
  'coxaDireita',
  'pernaEsquerda',
  'pernaDireita',
  'peEsquerdo',
  'peDireito',
]

export interface PropsCenaCinematica {
  tipoColisao: TipoColisao
  alvoImpacto: TipoAlvoImpacto
  tempo: number
  ocupantes: readonly Ocupante[]
  tonus?: TonusOcupante
  /** ocupante cujos rastros, vetor de deslocamento e eixo da coluna aparecem */
  idFoco: string
  mostrarVetores: boolean
  mostrarRastros: boolean
  mostrarEixoColuna: boolean
  /** velocidade do evento escolhida pelo aluno, em km/h */
  velocidadeImpacto: number
  /** física real para ocupantes e objetos; desligada, vale o caminho procedural */
  fisicaReal: boolean
  /** estado das peças, para os colisores refletirem reclinar/recuar/remover */
  estadosPecas: MapaEstados
  /** leitura do primeiro impacto de cada carga, para o painel */
  aoResolverFisica?: (resultado: ReturnType<typeof useFisicaCarga>) => void
  /** põe o ocupante em foco ao tocá-lo na cena, em vez de escolhê-lo na lista */
  aoSelecionarOcupante?: (id: string) => void
  /** Pistas de cena e marcadores de trauma */
  pistas?: readonly PistaCena[]
  pistaSelecionadaId?: string | null
  aoSelecionarPista?: (id: string | null) => void
  mostrarPistas?: boolean
  /** Variações cinematográficas */
  trajetoriaFrontal?: TrajetoriaFrontal
  smallOverlap?: boolean
  ladoLateral?: LadoImpactoLateral
  /** classe, velocidade, arranjo e ponto de contato do outro veículo */
  outroVeiculo?: ConfiguracaoOutroVeiculo
  /** mapa didático de deformação (zona, intensidade, direção da carga) */
  mostrarMapaDeformacao?: boolean
  /** timeline pausada: habilita o contorno das peças deformadas */
  pausado?: boolean
  /** lataria sólida: amassamentos na chapa + dano estrutural em raio-X */
  lataSolida?: boolean
  /** Modo Raio-X nos ocupantes: visualização de órgãos internos da 3ª Colisão */
  modoRaioX?: boolean
}

/**
 * Cinemática das quatro colisões, com todos os ocupantes da cena.
 *
 * Cada pessoa reage à MESMA timeline conforme sua própria retenção
 * (`reacaoDoOcupante`): com cinto, reproduz a curva didática de sempre; sem
 * cinto, continua com a velocidade que tinha até encontrar alguma superfície.
 * Carga solta (animal/objeto) usa exatamente o mesmo mecanismo, sempre sem
 * retenção — não é um segundo sistema, é o mesmo aplicado a outro corpo.
 *
 * Rastros, vetor de deslocamento e eixo da coluna só aparecem para o ocupante
 * em foco: com vários corpos na cena, sobrepor tudo isso para todos deixaria
 * de mostrar qualquer coisa com clareza.
 */
export function CenaCinematica(props: PropsCenaCinematica) {
  const { perfil } = useMaquete()
  const definicao = colisao(props.tipoColisao)
  const quadro = amostrarCinematica(props.tipoColisao, props.tempo, props.velocidadeImpacto)
  const posicaoVeiculo = posicaoDoVeiculo(perfil, quadro)

  /**
   * Deformação do evento inteiro, para todos os veículos: descritores puros
   * (domínio), calculados uma vez por configuração. O tempo só AMOSTRA —
   * por isso play, pausa, seek e reinício nunca acumulam deformação.
   */
  const evento = useMemo(
    () =>
      deformacaoDoEvento({
        tipo: props.tipoColisao,
        alvo: props.alvoImpacto,
        perfil,
        velocidadeKmh: props.velocidadeImpacto,
        smallOverlap: props.smallOverlap,
        outroVeiculo: props.outroVeiculo,
      }),
    [props.tipoColisao, props.alvoImpacto, perfil, props.velocidadeImpacto, props.smallOverlap, props.outroVeiculo],
  )
  // cada veículo lê SÓ o próprio estado (filtrado por veiculoId)
  const deformacaoPrincipal = estadoDeformacaoNoInstante(ID_VEICULO_PRINCIPAL, evento.descritores, props.tempo)
  const colisaoPrincipal = evento.colisaoPrincipal
  const colisaoSecundaria = evento.colisaoSecundaria
  const deformacaoOutro = colisaoPrincipal
    ? estadoDeformacaoNoInstante(colisaoPrincipal.idB, evento.descritores, props.tempo)
    : null
  const deformacaoOutroSecundario = colisaoSecundaria
    ? estadoDeformacaoNoInstante(colisaoSecundaria.idB, evento.descritores, props.tempo)
    : null
  const descritorPrincipal = evento.descritores.find((d) => d.veiculoId === ID_VEICULO_PRINCIPAL) ?? null
  // empurrão e guinada pós-impacto de A, quando o outro veículo transfere movimento
  const ajusteA = ajustePosImpactoA(colisaoPrincipal, props.tempo)

  const impactoSecundario = definicao.impactoSecundario
  const instanteSecundario = impactoSecundario ? instantesDeContato(props.tipoColisao)[1] : undefined
  const dinamicaSuspensao = useMemo(
    () => calcularDinamicaSuspensao(props.tipoColisao, props.tempo, props.velocidadeImpacto),
    [props.tipoColisao, props.tempo, props.velocidadeImpacto],
  )
  const ajusteVariacao = useMemo(
    () =>
      obterAjusteCinematicaVariacao(props.tipoColisao, props.tempo, {
        trajetoriaFrontal: props.trajetoriaFrontal,
        smallOverlap: props.smallOverlap,
        ladoLateral: props.ladoLateral,
      }),
    [props.tipoColisao, props.tempo, props.trajetoriaFrontal, props.smallOverlap, props.ladoLateral],
  )

  const ocupanteFoco = props.ocupantes.find((o) => o.id === props.idFoco) ?? props.ocupantes[0] ?? null

  const focoInfo = useMemo(() => {
    if (!ocupanteFoco || ocupanteFoco.tipo !== 'pessoa') return null
    const ancora = ancoraPorId(perfil, ocupanteFoco.idAncora)
    const proporcoes = perfilVitimaPorId(ocupanteFoco.idPerfil)
    return { ancora, proporcoes }
  }, [ocupanteFoco, perfil])

  /**
   * Rastros: amostram a MESMA reação do ocupante até o instante atual e
   * passam pela mesma transformação do veículo, então o rastro nunca discorda
   * da posição do corpo — sem cinto incluído.
   */
  const rastros = useMemo(() => {
    if (!props.mostrarRastros || props.tempo < 0.04 || !focoInfo || !ocupanteFoco) return null
    const { ancora, proporcoes } = focoInfo
    const superficies = superficiesDoHabitaculo(perfil, ancora)
    const origem = origemDeContatoPadrao(ancora, proporcoes)
    const passos = 30
    const cabeca: Vetor3[] = []
    const tronco: Vetor3[] = []
    for (let i = 0; i <= passos; i += 1) {
      const t = (Math.min(props.tempo, 1) * i) / passos
      const reacao = reacaoDoOcupante(props.tipoColisao, t, ocupanteFoco.retencao, superficies, origem)
      const quadroAmostra = amostrarCinematica(props.tipoColisao, t)
      const colocacao = {
        posicaoPelve: somar(ancora.posicao, reacao.deslocamentoRelativo),
        guinadaRad: 0,
      }
      const pose = {
        ...POSE_SENTADA,
        troncoInclinacaoRad: reacao.inclinacaoTroncoRad,
        troncoLateralRad: reacao.inclinacaoLateralTroncoRad,
        cabecaInclinacaoRad: reacao.inclinacaoCabecaRad,
      }
      cabeca.push(paraMundo(perfil, quadroAmostra, posicaoCabeca(colocacao, proporcoes, pose)))
      tronco.push(
        paraMundo(
          perfil,
          quadroAmostra,
          somar(colocacao.posicaoPelve, vetor(0, proporcoes.alturaPelve / 2 + proporcoes.alturaTronco / 2, 0)),
        ),
      )
    }
    return { cabeca, tronco }
  }, [props.mostrarRastros, props.tempo, props.tipoColisao, focoInfo, ocupanteFoco, perfil])

  const yReferencia = perfil.cinturaY + 0.25
  // a seta acompanha o veículo: na traseira ele é empurrado para longe do ponto
  // de contato, e uma seta fixa no mundo ficava boiando sozinha atrás da cena
  const origemImpacto = deslocarSeExistir(
    pontoDeOrigemDoImpacto(perfil, definicao.direcaoImpacto),
    vetor(posicaoVeiculo.x, 0, posicaoVeiculo.z),
  )
  const origemImpactoSecundario = impactoSecundario
    ? deslocarSeExistir(
        pontoDeOrigemDoImpacto(perfil, impactoSecundario.direcaoImpacto),
        vetor(posicaoVeiculo.x, 0, posicaoVeiculo.z),
      )
    : null

  // de onde cada carga solta parte: depende da lista inteira, porque dois corpos
  // no mesmo compartimento precisam ser separados um do outro
  const origensCarga = useMemo(() => origensDaCarga(perfil, props.ocupantes), [perfil, props.ocupantes])

  /**
   * Entrada da física real, memoizada: é a identidade dela que dispara uma nova
   * resolução, e resolver é caro o bastante para não caber num quadro de desenho.
   * `props.tempo` NÃO entra aqui de propósito — a trajetória é resolvida uma vez
   * para o evento inteiro e depois amostrada pelo tempo.
   */
  const entradaFisica = useMemo<EntradaSolucionador | null>(() => {
    if (!props.fisicaReal) return null
    const cargas = itensDeCarga(props.ocupantes)
      .map((item) => {
        const origem = origensCarga.get(item.id)
        return origem ? { id: item.id, corpo: item.corpo, origem } : null
      })
      .filter((c): c is { id: string; corpo: typeof c extends null ? never : ReturnType<typeof corpoSoltoPorId>; origem: Vetor3 } => c !== null)
    const pessoas = props.ocupantes
      .filter((o) => o.tipo === 'pessoa')
      .map((o) => ({
        id: o.id,
        ancora: ancoraPorId(perfil, o.idAncora),
        proporcoes: proporcoesPorId(o.idPerfil),
        retencao: o.retencao,
      }))

    // A física também precisa rodar quando há somente vítimas humanas.
    // Antes, a ausência de carga solta desligava o Rapier silenciosamente.
    if (cargas.length === 0 && pessoas.length === 0) return null

    return {
      perfil,
      estados: props.estadosPecas,
      tipoColisao: props.tipoColisao,
      alvoImpacto: props.alvoImpacto,
      velocidadeImpactoKmh: props.velocidadeImpacto,
      tonus: props.tonus,
      cargas,
      pessoas,
      // o veículo da física leva o mesmo movimento pós-impacto que a cena desenha
      colisaoEntreVeiculos: colisaoPrincipal,
    }
  }, [props.fisicaReal, props.ocupantes, props.estadosPecas, props.tipoColisao, props.alvoImpacto, props.velocidadeImpacto, props.tonus, perfil, origensCarga, colisaoPrincipal])

  const fisica = useFisicaCarga(entradaFisica)
  const aoResolver = props.aoResolverFisica
  useEffect(() => {
    aoResolver?.(fisica)
  }, [aoResolver, fisica])

  // uma carga com trajetória resolvida é desenhada em coordenadas de MUNDO, fora
  // do grupo do veículo: a engine já compôs o movimento do veículo nela
  const idsComFisica = useMemo(
    () => new Set((fisica.resultado?.trajetorias ?? []).map((t) => t.id)),
    [fisica.resultado],
  )

  // ponto do 1º contato no mundo, para o marcador de inspeção
  const pontoContatoMundo = useMemo<[number, number, number] | null>(() => {
    if (colisaoPrincipal) return [colisaoPrincipal.pontoContato.x, 0, colisaoPrincipal.pontoContato.z]
    if (!descritorPrincipal || descritorPrincipal.regiao === 'teto') return null
    const posContato = posicaoDoVeiculo(perfil, amostrarCinematica(props.tipoColisao, evento.instanteToque, props.velocidadeImpacto))
    const [x, , z] = descritorPrincipal.centroLocal
    return [posContato.x + x, 0, posContato.z + z]
  }, [colisaoPrincipal, descritorPrincipal, perfil, props.tipoColisao, evento.instanteToque, props.velocidadeImpacto])

  // A e as vítimas desenhadas no mundo giram juntos: guinada do small overlap
  // e ajuste pós-impacto aplicados em torno da origem do veículo
  const guinadaExtraA = ajusteVariacao.deltaYaw + ajusteA.guinadaRad
  const pivoA = somar(posicaoVeiculo, ajusteA.deslocamento)
  const distanciaRodada = quadro.posicaoVeiculo.z - amostrarCinematica(props.tipoColisao, 0, props.velocidadeImpacto).posicaoVeiculo.z + ajusteA.deslocamento.z

  const reacaoFoco =
    focoInfo && ocupanteFoco
      ? reacaoDoOcupante(
          props.tipoColisao,
          props.tempo,
          ocupanteFoco.retencao,
          superficiesDoHabitaculo(perfil, focoInfo.ancora),
          origemDeContatoPadrao(focoInfo.ancora, focoInfo.proporcoes),
        )
      : null

  return (
    <group>
      <AlvoImpacto3D
        tipo={props.alvoImpacto}
        direcaoImpacto={definicao.direcaoImpacto}
        perfil={perfil}
        tempo={props.tempo}
        trajetoriaFisica={fisica.resultado?.trajetoriaAlvo}
        tipoColisao={props.tipoColisao}
        velocidadeImpacto={props.velocidadeImpacto}
        instanteContato={evento.instanteToque}
        colisaoVeiculos={colisaoPrincipal}
        deformacaoOutro={deformacaoOutro}
        centroContato={evento.centroContatoPrincipal}
        larguraContato={descritorPrincipal?.larguraM}
        smallOverlap={Boolean(descritorPrincipal?.smallOverlap)}
        mostrarMapaDeformacao={props.mostrarMapaDeformacao}
      />
      {impactoSecundario && instanteSecundario != null && (
        <AlvoImpacto3D
          tipo={impactoSecundario.alvo}
          direcaoImpacto={impactoSecundario.direcaoImpacto}
          perfil={perfil}
          tempo={props.tempo}
          trajetoriaFisica={fisica.resultado?.trajetoriaAlvoSecundario}
          tipoColisao={props.tipoColisao}
          velocidadeImpacto={props.velocidadeImpacto}
          instanteContato={instanteSecundario}
          colisaoVeiculos={colisaoSecundaria}
          deformacaoOutro={deformacaoOutroSecundario}
          mostrarMapaDeformacao={props.mostrarMapaDeformacao}
        />
      )}
      <DestaqueImpacto3D
        pontoContato={props.mostrarMapaDeformacao && props.tempo >= evento.instanteToque ? pontoContatoMundo : null}
        contornar={Boolean(props.mostrarMapaDeformacao && props.pausado && deformacaoPrincipal.campos.length > 0)}
        chave={`${deformacaoPrincipal.chave}|${deformacaoOutro?.chave ?? ''}`}
      />
      {/* Rodas acompanham a pose base; mergulho e balanço da carroceria são absorvidos pela suspensão. */}
      <group name="rodas-apoiadas-cinematica" position={paraArray(pivoA)}
        rotation={[quadro.rotacaoVeiculoRad.x, quadro.rotacaoVeiculoRad.y + guinadaExtraA, quadro.rotacaoVeiculoRad.z]}>
        <ProvedorDeformacao estado={deformacaoPrincipal} perfil={perfil} raioXEstrutural={props.lataSolida}>
          <RodaRealista perfil={perfil} distanciaRodada={distanciaRodada} />
          <SuspensaoVeicular perfil={perfil} compressaoDianteira={dinamicaSuspensao.compressaoDianteira}
            compressaoTraseira={dinamicaSuspensao.compressaoTraseira} />
        </ProvedorDeformacao>
      </group>
      <group
        position={paraArray(pivoA)}
        rotation={[
          quadro.rotacaoVeiculoRad.x + dinamicaSuspensao.pitchRad,
          quadro.rotacaoVeiculoRad.y + guinadaExtraA,
          quadro.rotacaoVeiculoRad.z + dinamicaSuspensao.rollRad,
        ]}
      >
        <VeiculoDidatico
          perfil={perfil}
          mostrarRodas={false}
          mostrarSuspensao={false}
          deformacao={deformacaoPrincipal}
          raioXEstrutural={props.lataSolida}
        />
        {/* mapa de deformação: sobreposição didática opcional, não é a lataria */}
        {props.mostrarMapaDeformacao && (
          <DeformacaoImpacto3D perfil={perfil} estado={deformacaoPrincipal} rotulo={colisaoPrincipal ? 'A' : undefined} />
        )}
        <PistasCena3D
          pistas={props.pistas ?? []}
          pistaSelecionadaId={props.pistaSelecionadaId ?? null}
          aoSelecionarPista={props.aoSelecionarPista ?? (() => {})}
          visivel={props.mostrarPistas !== false}
        />
        {props.ocupantes
          .filter((ocupante) => !idsComFisica.has(ocupante.id))
          .map((ocupante) => (
          <OcupanteSelecionavel
            key={ocupante.id}
            id={ocupante.id}
            aoSelecionar={props.aoSelecionarOcupante}
          >
            <CorpoDoOcupante
              ocupante={ocupante}
              tipoColisao={props.tipoColisao}
              tempo={props.tempo}
              perfil={perfil}
              origemCarga={origensCarga.get(ocupante.id) ?? null}
              emFoco={ocupante.id === ocupanteFoco?.id}
              mostrarEixoColuna={props.mostrarEixoColuna}
              trajetoriaFrontal={props.trajetoriaFrontal}
              modoRaioX={props.modoRaioX}
            />
          </OcupanteSelecionavel>
        ))}
      </group>

      {/* corpos com física real vivem no mundo e já trazem o empurrão pós-impacto
          (a física o recebe em `colisaoEntreVeiculos`); o grupo só aplica a eles
          a guinada visual que o veículo recebeu, em torno da origem dele */}
      <group position={paraArray(pivoA)} rotation={[0, guinadaExtraA, 0]}>
      <group position={paraArray(multiplicar(pivoA, -1))}>
      {fisica.resultado?.trajetorias.map((trajetoria) => {
        const ocupante = props.ocupantes.find((o) => o.id === trajetoria.id)
        if (!ocupante) return null
        if (ocupante.tipo === 'pessoa') {
          const todas = fisica.resultado?.trajetorias ?? []
          const tronco = trajetoriaPorId(todas, idSegmentoRagdoll(ocupante.id, 'tronco'))
          const cabeca = trajetoriaPorId(todas, idSegmentoRagdoll(ocupante.id, 'cabeca'))
          if (!tronco || !cabeca) return null
          const membros = Object.fromEntries(
            SEGMENTOS_MEMBROS.flatMap((segmento) => {
              const trajetoriaMembro = trajetoriaPorId(
                todas,
                idSegmentoRagdoll(ocupante.id, segmento),
              )
              return trajetoriaMembro
                ? [[segmento, amostrarTrajetoria(trajetoriaMembro, props.tempo)]]
                : []
            }),
          ) as Partial<Record<MembroAprisionavel, QuadroCorpo>>
          const aprisionamentos = (fisica.resultado?.aprisionamentos ?? []).filter(
            (item) => item.idOcupante === ocupante.id && item.tempo <= props.tempo,
          )
          const quadrosBio = fisica.resultado?.estresseBiomecanico?.[ocupante.id]?.quadros
          const indiceBio = quadrosBio && quadrosBio.length > 0
            ? Math.min(Math.round(props.tempo * (quadrosBio.length - 1)), quadrosBio.length - 1)
            : -1
          const leituraBiomecanica = indiceBio >= 0 ? quadrosBio![indiceBio] : undefined

          return (
            <OcupanteSelecionavel
              key={trajetoria.id}
              id={ocupante.id}
              aoSelecionar={props.aoSelecionarOcupante}
            >
            <PessoaFisica
              proporcoes={perfilVitimaPorId(ocupante.idPerfil)}
              pelve={trajetoria}
              tronco={tronco}
              cabeca={cabeca}
              membros={membros}
              tempo={props.tempo}
              aprisionamentos={aprisionamentos}
              leituraBiomecanica={leituraBiomecanica}
              // o instante é obrigatório: sem ele as regiões corporais acendiam
              // com a régua em zero, antes de qualquer contato
              atencoes={avaliarAtencaoCorporal(fisica.resultado?.impactos ?? [], ocupante.id, props.tempo)}
              mostrarEixoColuna={props.mostrarEixoColuna && ocupante.id === ocupanteFoco?.id}
              modoRaioX={props.modoRaioX}
            />
            </OcupanteSelecionavel>
          )
        }
        return (
          <OcupanteSelecionavel
            key={trajetoria.id}
            id={ocupante.id}
            aoSelecionar={props.aoSelecionarOcupante}
          >
            <CorpoSoltoFisico
              corpo={corpoSoltoPorId(ocupante.idPerfil)}
              trajetoria={trajetoria}
              tempo={props.tempo}
            />
          </OcupanteSelecionavel>
        )
      })}
      </group>
      </group>

      {rastros && (
        <>
          {rastros.cabeca.length > 1 && (
            <Line points={rastros.cabeca.map(paraArray)} color={CORES_SEMANTICAS.rastro} lineWidth={4} />
          )}
          {rastros.tronco.length > 1 && (
            <Line
              points={rastros.tronco.map(paraArray)}
              color={CORES_SEMANTICAS.trajetoria}
              lineWidth={3}
              dashed
              dashSize={0.06}
              gapSize={0.05}
            />
          )}
        </>
      )}

      {props.mostrarVetores && (
        <>
          {/* deslocamento do veículo: some quando o veículo para */}
          {quadro.velocidadeVeiculo > 0.02 && (
            <Vetor3D
              origem={vetor(perfil.largura / 2 + 0.45, yReferencia, posicaoVeiculo.z - 0.3)}
              destino={vetor(
                perfil.largura / 2 + 0.45,
                yReferencia,
                posicaoVeiculo.z - 0.3 + 0.35 + quadro.velocidadeVeiculo * 1.3,
              )}
              cor={CORES_SEMANTICAS.trajetoria}
            />
          )}

          {/* direção do impacto: tombamento e capotamento não têm uma, e não inventam */}
          {definicao.direcaoImpacto && origemImpacto && (
            <>
              <Vetor3D
                origem={somar(origemImpacto, multiplicar(definicao.direcaoImpacto, -0.2))}
                destino={somar(origemImpacto, multiplicar(definicao.direcaoImpacto, 1.15))}
                cor={CORES_SEMANTICAS.impacto}
                espessura={6}
              />
              <Rotulo3D
                posicao={somar(origemImpacto, vetor(0, 0.3, 0))}
                texto="direção do impacto"
                variante="risco"
                fator={5}
              />
            </>
          )}

          {/* segunda seta, só perto do segundo contato: duas setas desde t=0
              tornariam o engavetamento ilegível antes de a cadeia acontecer */}
          {impactoSecundario &&
            origemImpactoSecundario &&
            instanteSecundario != null &&
            props.tempo >= instanteSecundario - 0.08 && (
              <>
                <Vetor3D
                  origem={somar(origemImpactoSecundario, multiplicar(impactoSecundario.direcaoImpacto, -0.2))}
                  destino={somar(origemImpactoSecundario, multiplicar(impactoSecundario.direcaoImpacto, 1.15))}
                  cor={CORES_SEMANTICAS.impacto}
                  espessura={6}
                />
                <Rotulo3D
                  posicao={somar(origemImpactoSecundario, vetor(0, 0.3, 0))}
                  texto="2º contato — direção do impacto"
                  variante="risco"
                  fator={5}
                />
              </>
            )}

          {focoInfo && reacaoFoco && (
            <DeslocamentoRelativo
              deslocamento={reacaoFoco.deslocamentoRelativo}
              base={somar(
                posicaoVeiculo,
                vetor(focoInfo.ancora.posicao.x, focoInfo.ancora.posicao.y + 0.6, focoInfo.ancora.posicao.z),
              )}
            />
          )}
        </>
      )}

      <Rotulo3D
        posicao={somar(posicaoVeiculo, vetor(0, perfil.tetoY + 0.5, 0))}
        texto={quadro.fase}
        variante="neutro"
        fator={5}
      />
      {colisaoPrincipal && (
        <Rotulo3D
          posicao={somar(pivoA, vetor(0, perfil.tetoY + 0.78, 0))}
          texto={`A • ${CLASSES_VEICULO[colisaoPrincipal.classeA].nome.toLowerCase()} • ${Math.round(colisaoPrincipal.velocidadeAKmh)} km/h`}
          variante="estrutura"
          fator={5}
        />
      )}

      {reacaoFoco?.contato && (
        <Rotulo3D
          posicao={somar(posicaoVeiculo, vetor(0, perfil.tetoY + 0.24, 0))}
          texto={`contato: ${reacaoFoco.contato.superficie.nome.toLowerCase()}`}
          variante="risco"
          fator={5}
        />
      )}
      {/* a estimativa só fala quando não há medida: com a física ligada, quem diz
          se o corpo saiu do envelope é `resultado.ejecoes`, e afirmar ejeção com
          o corpo visivelmente deitado sobre o teto contradiz a própria cena */}
      {reacaoFoco?.ejetado && !props.fisicaReal && (
        <Rotulo3D
          posicao={somar(posicaoVeiculo, vetor(0, perfil.tetoY + 0.24, 0))}
          texto="ejeção — nada deteve o corpo"
          variante="risco"
          fator={5}
        />
      )}
    </group>
  )
}

/** Um corpo (pessoa, animal ou objeto) posicionado conforme sua própria reação. */
function CorpoDoOcupante({
  ocupante,
  tipoColisao,
  tempo,
  perfil,
  origemCarga,
  emFoco,
  mostrarEixoColuna,
  trajetoriaFrontal,
  modoRaioX = false,
}: {
  ocupante: Ocupante
  tipoColisao: TipoColisao
  tempo: number
  perfil: ReturnType<typeof useMaquete>['perfil']
  /** origem já empacotada da carga solta; nulo para pessoa */
  origemCarga: Vetor3 | null
  emFoco: boolean
  mostrarEixoColuna: boolean
  trajetoriaFrontal?: TrajetoriaFrontal
  modoRaioX?: boolean
}) {
  const ancora = ancoraPorId(perfil, ocupante.idAncora)

  if (ocupante.tipo === 'pessoa') {
    const proporcoes = perfilVitimaPorId(ocupante.idPerfil)
    const superficies = superficiesDoHabitaculo(perfil, ancora)
    const origem = origemDeContatoPadrao(ancora, proporcoes)
    const reacao = reacaoDoOcupante(tipoColisao, tempo, ocupante.retencao, superficies, origem)

    const ajuste =
      ocupante.retencao === 'semCinto' && (tipoColisao === 'frontal' || tipoColisao === 'frontalComTraseira')
        ? obterAjusteCinematicaVariacao(tipoColisao, tempo, { trajetoriaFrontal })
        : null

    const deslocPelve = ajuste ? somar(reacao.deslocamentoRelativo, ajuste.deltaPosicaoPelve) : reacao.deslocamentoRelativo
    const colocacao = { posicaoPelve: somar(ancora.posicao, deslocPelve), guinadaRad: 0 }
    const pose = {
      ...POSE_SENTADA,
      troncoInclinacaoRad:
        reacao.inclinacaoTroncoRad +
        (ajuste && trajetoriaFrontal === 'downAndUnder' ? -0.15 * Math.min(1, tempo / 0.5) : 0),
      troncoLateralRad: reacao.inclinacaoLateralTroncoRad,
      cabecaInclinacaoRad:
        reacao.inclinacaoCabecaRad +
        (ajuste && trajetoriaFrontal === 'downAndUnder' ? 0.2 * Math.min(1, tempo / 0.5) : 0),
    }
    return (
      <VitimaProcedural
        proporcoes={proporcoes}
        colocacao={colocacao}
        pose={pose}
        mostrarEixoColuna={mostrarEixoColuna && emFoco}
        modoRaioX={modoRaioX}
      />
    )
  }

  const corpo = corpoSoltoPorId(ocupante.idPerfil)
  // a carga solta parte do compartimento em que estava, não da âncora do
  // motorista: era isso que punha a caixa do porta-malas em cima da vítima
  const origem = origemCarga ?? somar(ancora.posicao, vetor(0, corpo.dimensoes.y / 2 + 0.05, 0))
  const superficies: readonly SuperficieInterna[] = superficiesDoHabitaculo(perfil, ancora)
  const reacaoCarga: ReacaoOcupante = reacaoDoOcupante(tipoColisao, tempo, 'semCinto', superficies, origem)
  return <CorpoSoltoProcedural corpo={corpo} posicao={somar(origem, reacaoCarga.deslocamentoRelativo)} />
}

/** De onde parte a seta do impacto: a extremidade do veículo que é atingida. */
function pontoDeOrigemDoImpacto(
  perfil: Parameters<typeof posicaoDoVeiculo>[0],
  direcao: Vetor3 | null,
): Vetor3 | null {
  if (!direcao) return null
  const y = perfil.cinturaY + 0.25
  if (Math.abs(direcao.z) > Math.abs(direcao.x)) {
    // vem da frente (direção −Z) ou da traseira (direção +Z)
    return direcao.z < 0
      ? vetor(0, y, perfil.frenteZ + 1.5)
      : vetor(0, y, perfil.traseiraZ - 1.5)
  }
  const sinal = direcao.x > 0 ? -1 : 1
  return vetor(sinal * (perfil.largura / 2 + 1.5), y, perfil.ancoras[0]!.posicao.z)
}

const deslocarSeExistir = (p: Vetor3 | null, d: Vetor3): Vetor3 | null =>
  p === null ? null : somar(p, d)

/** Seta do deslocamento do ocupante em relação ao veículo, na direção em que ele ocorre. */
function DeslocamentoRelativo({ deslocamento, base }: { deslocamento: Vetor3; base: Vetor3 }) {
  const tamanho = Math.hypot(deslocamento.x, deslocamento.y, deslocamento.z)
  if (tamanho < 0.02) return null
  const escala = (tamanho + 0.12) / tamanho
  return (
    <Vetor3D
      origem={base}
      destino={somar(base, multiplicar(deslocamento, escala))}
      cor={CORES_SEMANTICAS.rastro}
      espessura={4}
    />
  )
}

/**
 * Carga solta com trajetória resolvida pela engine.
 *
 * Desenhada em coordenadas de MUNDO, fora do grupo do veículo: a simulação já
 * compôs o movimento do veículo dentro da trajetória, e colocá-la dentro do
 * grupo aplicaria a mesma transformação duas vezes.
 *
 * A malha é a mesma de sempre (`CorpoSoltoProcedural`); o que mudou é de onde vem
 * a posição — e que agora há orientação, porque um corpo com volume tomba.
 */
function CorpoSoltoFisico({
  corpo,
  trajetoria,
  tempo,
}: {
  corpo: CorpoSolto
  trajetoria: TrajetoriaCorpo
  tempo: number
}) {
  const quadro = amostrarTrajetoria(trajetoria, tempo)
  return (
    <group
      position={paraArray(quadro.posicao)}
      quaternion={[quadro.rotacao.x, quadro.rotacao.y, quadro.rotacao.z, quadro.rotacao.w]}
    >
      {/* a malha desenha na origem local: posição e rotação vivem no grupo */}
      <CorpoSoltoProcedural corpo={corpo} posicao={vetor(0, 0, 0)} />
    </group>
  )
}

/**
 * Ocupante articulado com transformações independentes da pelve, tórax e cabeça.
 * Este componente só é usado na cinemática; a extração mantém o rig controlado.
 */
function PessoaFisica({
  proporcoes,
  pelve,
  tronco,
  cabeca,
  membros,
  tempo,
  atencoes,
  aprisionamentos,
  mostrarEixoColuna,
  leituraBiomecanica,
  modoRaioX,
}: {
  proporcoes: ReturnType<typeof perfilVitimaPorId>
  pelve: TrajetoriaCorpo
  tronco: TrajetoriaCorpo
  cabeca: TrajetoriaCorpo
  membros: Partial<Record<MembroAprisionavel, QuadroCorpo>>
  tempo: number
  atencoes: readonly AtencaoCorporal[]
  aprisionamentos: readonly AprisionamentoMembro[]
  mostrarEixoColuna: boolean
  leituraBiomecanica?: LeituraBiomecanicaInstante
  modoRaioX?: boolean
}) {
  // a pose é a da física do início ao fim: o corpo termina onde cinto, airbag,
  // banco e o tônus do pescoço o deixaram (ver `reacaoSrs.test.ts`)
  const quadroTronco = amostrarTrajetoria(tronco, tempo)
  const quadroCabeca = amostrarTrajetoria(cabeca, tempo)
  return (
    <VitimaRagdoll
      proporcoes={proporcoes}
      pelve={amostrarTrajetoria(pelve, tempo)}
      tronco={quadroTronco}
      cabeca={quadroCabeca}
      membros={membros}
      atencoes={atencoes}
      aprisionamentos={aprisionamentos}
      mostrarEixoColuna={mostrarEixoColuna}
      leituraBiomecanica={leituraBiomecanica}
      modoRaioX={modoRaioX}
    />
  )
}

